import React, { ReactNode, useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { cn } from '@/features/utils'
import { updateUrlQuery } from 'modules/utils'
import { SidebarProvider, SidebarInset, SidebarTrigger, useSidebar } from '@/features/shadcn/ui/sidebar'
import { Separator } from '@/features/shadcn/ui/separator'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/features/shadcn/ui/breadcrumb'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/features/shadcn/ui/dropdown-menu'
import { ChevronDown, ExternalLink } from 'lucide-react'
import {
  teamNavLabel,
} from '../../applications/Settings/views/SharedAccounts/teamPagePermissions'
import {
  selectIsWorkspaceContext,
  selectOrganizations,
} from '../../features/organizations/organizationsSlice'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/features/shadcn/ui/tooltip'
import AppSidebar from 'sharedComponents/AppSidebar'
import IconLogo from '../../assets/images/PayHelm_Icon_PNG.png'
import { customTabView } from '../../applications/Dashboard/dashboardTabRouting'
import { sortedDashboardTabs, withDisambiguatedTabTitles } from '../../modules/widgetSpec'

/** Compact header brand on compact layout. Stays fixed when the sheet opens so the bar does not flash; the open sidebar shows the full wordmark. */
function MobileHeaderBrand() {
  const navigate = useNavigate()
  const { isMobile } = useSidebar()

  if (!isMobile) return null

  return (
    <a
      href="/dashboard"
      className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-1.5 tw-no-underline hover:tw-opacity-90"
      onClick={(e) => {
        e.preventDefault()
        navigate('/dashboard')
      }}
    >
      <img src={IconLogo} alt="PayHelm" className="tw-h-7 tw-w-7 tw-object-contain" />
    </a>
  )
}

// Map of route paths to display names
const routeNames: Record<string, string> = {
  dashboard: 'Dashboard',
  traffic: 'Traffic',
  reports: 'Reports',
  integrations: 'Integrations',
  custom: 'Custom Reports',
  ai: 'AI Chat',
  emailcampaigns: 'Email Campaigns',
  shipping: 'Shipping',
  inventory: 'Inventory',
  plan: 'My Plan',
  profile: 'Profile',
  settings: 'Settings',
  staff: 'Staff',
  statistics: 'Statistics',
  operations: 'Operations Overview',
  analytics: 'Analytics',
  acquisition: 'Acquisition',
  attribution: 'Attribution',
  workflows: 'Workflows',
  backfills: 'Jobs',
  onboard: 'Onboarding',
  channels: 'Channels',
  faq: 'FAQ',
  claims: 'Claims',
  pipeline: 'Pipeline',
  admin: 'Admin',
  users: 'Users',
  permissions: 'Roles & Permissions',
  policies: 'Policies',
  opportunities: 'Opportunities',
  health: 'System Health',
  explorer: 'Explorer',
  tools: 'Tool Runner',
  reference: 'Reference',
  audit: 'Audit Log',
  channeladvisor: 'ChannelAdvisor',
}

// Map of view query params to display names for Dashboard
const dashboardViews: Record<string, string> = {
  overview: 'Overview',
  orders: 'Orders',
  products: 'Products',
  customer: 'Customers',
  marketing: 'Marketing',
  map: 'Map',
}

// Map of view query params to display names for Traffic
const trafficViews: Record<string, string> = {
  metrics: 'Overview',
  ads: 'Ads',
  overview: 'Trends',
  orders: 'Orders',
  checkouts: 'Checkouts',
  carts: 'Carts',
  visitors: 'Visitors',
  sessions: 'Sessions',
}

// Map of tab query params to display names for Shipping
const shippingTabs: Record<string, string> = {
  shipped: 'Shipped',
  awaiting: 'Awaiting Fulfillment',
}

// Map of view query params to display names for Settings (shared_accounts label resolved in component)
const baseSettingsViews: Record<string, string> = {
  profile: 'Profile',
  accounts: 'Seller Accounts',
  shared_accounts: 'My Team',
  data_connectors: 'Data Connectors',
  preferences: 'Preferences',
}

// Map of view query params to display names for My Plan
const planViews: Record<string, string> = {
  options: 'Options',
  'add-ons': 'Add-Ons',
  billing: 'Billing',
  invoices: 'Invoices',
  paymentHistory: 'Payment History',
}

const planViewsHiddenForShopifyBreadcrumb = new Set(['add-ons', 'billing', 'invoices'])

interface SidebarLayoutProps {
  children: ReactNode
  hasAccounts: boolean
  isLite: boolean
  accessPermissions: Record<string, boolean>
}

const SidebarLayout: React.FC<SidebarLayoutProps> = ({
  children,
  hasAccounts,
  isLite,
  accessPermissions,
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const customReports = useSelector((state: any) => state.customReports?.reports)
  const reportsData = useSelector((state: any) => state.reports?.reportData)
  const billingChannel = useSelector((state: any) => state.plan?.billingChannel)
  const isWorkspaceContext = useSelector(selectIsWorkspaceContext)
  const organizations = useSelector(selectOrganizations)
  const activeOrg = organizations.find((org) => org.is_active)
  const inOrgContext = isWorkspaceContext && !!activeOrg
  const customDashboardTabsConfig = useSelector(
    (state: any) => state.root?.user?.dashboardconfig?.tabs
  )
  const dashboardViewLabels = useMemo(
    () => ({
      ...dashboardViews,
      ...Object.fromEntries(
        withDisambiguatedTabTitles(sortedDashboardTabs(customDashboardTabsConfig)).map((tab) => [
          customTabView(tab.id),
          tab.displayTitle,
        ])
      ),
    }),
    [customDashboardTabsConfig]
  )

  const settingsViews = useMemo(
    () => ({
      ...baseSettingsViews,
      shared_accounts: teamNavLabel(inOrgContext),
    }),
    [inOrgContext]
  )

  // Parse the current path and query
  const pathSegments = location.pathname.split('/').filter(Boolean)
  const searchParams = new URLSearchParams(location.search)
  const viewParam = searchParams.get('view')

  // Build breadcrumb items
  const breadcrumbs: {
    label: string
    href?: string
    hasDropdown?: boolean
    dropdownItems?: { label: string; view: string }[]
  }[] = []

  if (pathSegments.length > 0) {
    const mainRoute = pathSegments[0]
    const mainLabel = routeNames[mainRoute] || mainRoute.charAt(0).toUpperCase() + mainRoute.slice(1)

    // Add main route
    if (viewParam && (mainRoute === 'dashboard' || mainRoute === 'traffic' || mainRoute === 'settings' || mainRoute === 'plan')) {
      // For settings with extracted views (My Team, Preferences), show only the view label—no "Settings" breadcrumb
      const settingsExtractedViews = ['profile', 'shared_accounts', 'preferences']
      const isSettingsExtracted =
        mainRoute === 'settings' && settingsExtractedViews.includes(viewParam)

      if (isSettingsExtracted) {
        const viewLabel = settingsViews[viewParam] || viewParam
        breadcrumbs.push({ label: viewLabel })
      } else {
        // If there's a view param, main route is a link with dropdown
        const planViewsForNav =
          billingChannel === 'shopify'
            ? Object.fromEntries(
                Object.entries(planViews).filter(([key]) => !planViewsHiddenForShopifyBreadcrumb.has(key)),
              )
            : planViews
        const viewLabels =
          mainRoute === 'dashboard'
            ? dashboardViewLabels
            : mainRoute === 'traffic'
              ? trafficViews
              : mainRoute === 'settings'
                ? settingsViews
                : planViewsForNav
        const dropdownItems = Object.entries(viewLabels).map(([key, label]) => ({
          label,
          view: key,
        }))
        breadcrumbs.push({
          label: mainLabel,
          href: `/${mainRoute}`,
          hasDropdown: true,
          dropdownItems,
        })

        // Add view as current page
        const viewLabel = viewLabels[viewParam] || viewParam
        breadcrumbs.push({ label: viewLabel })
      }
    } else if (
      mainRoute === 'shipping' &&
      (pathSegments[1] === 'settings' || pathSegments[1] === 'orders' || pathSegments[1] === 'shipments')
    ) {
      // Path-based shipping routes. The dropdown switches between Orders and the
      // Shipments views; `view` carries the workbench mode that the onClick maps
      // to a canonical path.
      const dropdownItems = [
        { label: 'Orders', view: 'awaiting' },
        { label: 'Shipments', view: 'transit' },
        { label: 'Insured', view: 'insured' },
        { label: 'Claims', view: 'claims' },
      ]
      breadcrumbs.push({
        label: mainLabel,
        href: '/shipping/orders',
        hasDropdown: true,
        dropdownItems,
      })
      let current = 'Orders'
      let currentHref: string | undefined
      if (pathSegments[1] === 'settings') {
        current = 'Settings'
      } else if (pathSegments[1] === 'shipments') {
        current = viewParam === 'insured' ? 'Insured' : viewParam === 'claims' ? 'Claims' : 'Shipments'
        currentHref = viewParam ? `/shipping/shipments?view=${viewParam}` : '/shipping/shipments'
      } else if (pathSegments[1] === 'orders') {
        currentHref = '/shipping/orders'
      }

      // Deep workflow routes add a final step crumb and turn the list crumb into
      // a link so the merchant always knows where they are and can step back out.
      let workflowLabel: string | null = null
      if (pathSegments[1] === 'orders' && pathSegments[3] === 'label') {
        workflowLabel = 'Purchase label'
      } else if (pathSegments[1] === 'shipments' && pathSegments[3] === 'insure') {
        workflowLabel = 'Purchase insurance'
      } else if (pathSegments[1] === 'shipments' && pathSegments[3] === 'claim') {
        workflowLabel = 'File claim'
      } else if (
        pathSegments[1] === 'shipments' &&
        pathSegments.length === 3 &&
        pathSegments[2]
      ) {
        workflowLabel = 'Shipment details'
      }

      if (workflowLabel) {
        breadcrumbs.push({ label: current, href: currentHref })
        // Insure / claim launched from a shipment's detail page are logically
        // nested under it. Surface a clickable "Shipment details" crumb (using
        // the provenance stashed in router state) so the trail reads
        // Shipments › Shipment details › Purchase insurance and stays in sync
        // with the in-page "Back to shipment details" control.
        const fromDetailId = (location.state as { shippingFromDetailId?: string } | null)
          ?.shippingFromDetailId
        if (
          fromDetailId &&
          (workflowLabel === 'Purchase insurance' || workflowLabel === 'File claim')
        ) {
          breadcrumbs.push({
            label: 'Shipment details',
            href: `/shipping/shipments/${encodeURIComponent(fromDetailId)}`,
          })
        }
        breadcrumbs.push({ label: workflowLabel })
      } else {
        breadcrumbs.push({ label: current })
      }
    } else if (mainRoute === 'reports' && pathSegments[1]) {
      const reportId = pathSegments[1]
      const reportName = reportsData?.[reportId]?.title || 'Report'
      breadcrumbs.push({ label: 'Reports', href: '/reports' })
      breadcrumbs.push({ label: reportName })
    } else if (mainRoute === 'custom' && pathSegments[1]) {
      // Dashboard report view: Reports > Dashboard Reports > Report Name
      const reportId = pathSegments[1]
      const selectedReport = (customReports || []).find((r: { id: number }) => String(r.id) === reportId)
      const reportName = selectedReport?.name || 'Report'
      breadcrumbs.push({ label: 'Reports', href: '/reports' })
      breadcrumbs.push({ label: 'Dashboard Reports', href: '/reports' })
      breadcrumbs.push({ label: reportName })
    } else if (mainRoute === 'admin') {
      breadcrumbs.push({ label: 'Admin', href: '/admin' })
      if (pathSegments[1]) {
        const subSection = pathSegments[1]
        const subLabel = routeNames[subSection] || subSection.charAt(0).toUpperCase() + subSection.slice(1)
        if (pathSegments.length > 2) {
          breadcrumbs.push({ label: subLabel, href: `/admin/${subSection}` })
          const deepSegment = pathSegments[2]
          const deepLabel = routeNames[deepSegment] || deepSegment.charAt(0).toUpperCase() + deepSegment.slice(1)
          if (deepSegment === 'account' && pathSegments[3]) {
            breadcrumbs.push({ label: 'Account' })
          } else {
            breadcrumbs.push({ label: deepLabel })
          }
        } else {
          breadcrumbs.push({ label: subLabel })
        }
      }
    } else {
      breadcrumbs.push({ label: mainLabel })
    }
  }

  const isAI = pathSegments[0] === 'ai'

  const [isInIframe, setIsInIframe] = useState(false)
  // True when embedded inside the Shopify admin app iframe (App Bridge injects
  // window.shopify). We hide the "Open in new tab" popout there — Shopify owns
  // that chrome and breaking out of its iframe isn't the intended UX.
  const [isShopifyEmbedded, setIsShopifyEmbedded] = useState(false)
  useEffect(() => {
    setIsInIframe(typeof window !== 'undefined' && window.self !== window.top)
    setIsShopifyEmbedded(typeof window !== 'undefined' && !!window.shopify)
  }, [])

  const handlePopout = () => {
    window.open(window.location.href, '_blank', 'noopener,noreferrer')
  }

  return (
    <SidebarProvider defaultOpen={true} className="tw-h-full tw-min-h-0 tw-flex-1">
      <AppSidebar
        hasAccounts={hasAccounts}
        isLite={isLite}
        accessPermissions={accessPermissions}
      />
      <SidebarInset
        className={cn(
          isAI && 'sidebar-inset--ai tw-h-full tw-max-h-none tw-min-h-0'
        )}
      >
        <header className="tw-flex tw-min-h-12 tw-shrink-0 tw-flex-wrap tw-items-center tw-gap-2 tw-border-b tw-bg-white tw-px-3 tw-py-1.5 max-md:tw-gap-y-2 md:tw-px-4 tw-sticky tw-top-0 tw-z-50 tw-min-w-0 tw-max-w-full tw-overflow-x-hidden">
          <SidebarTrigger className="tw--ml-1 tw-flex-shrink-0" />
          <MobileHeaderBrand />
          <Separator orientation="vertical" className="tw-mr-2 tw-h-4 tw-flex-shrink-0 max-md:tw-hidden" />
          <Separator orientation="vertical" className="tw-mr-2 tw-h-4 tw-flex-shrink-0 md:tw-hidden" />
          <div className="tw-min-w-0 tw-flex-1 tw-basis-0 tw-overflow-hidden">
            <Breadcrumb>
                <BreadcrumbList>
                  {breadcrumbs.map((crumb, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <BreadcrumbSeparator />}
                      <BreadcrumbItem>
                        {crumb.href ? (
                          crumb.hasDropdown && crumb.dropdownItems ? (
                            <DropdownMenu modal={false}>
                              <DropdownMenuTrigger className="tw-flex tw-items-center tw-gap-1 tw-no-underline hover:tw-underline">
                                {crumb.label}
                                <ChevronDown className="tw-h-4 tw-w-4" />
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start">
                                {crumb.dropdownItems.map((item, itemIndex) => (
                                  <DropdownMenuItem
                                    key={itemIndex}
                                    className="tw-cursor-pointer"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      const currentRoute = location.pathname.split('/').filter(Boolean)[0]
                                      if (currentRoute === 'shipping') {
                                        // Shipping is path-based: map the workbench mode to its canonical path.
                                        const path =
                                          item.view === 'awaiting'
                                            ? '/shipping/orders'
                                            : item.view === 'transit'
                                              ? '/shipping/shipments'
                                              : `/shipping/shipments?view=${item.view}`
                                        navigate(path)
                                        return
                                      }
                                      updateUrlQuery({ view: item.view })
                                    }}
                                  >
                                    {item.label}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : (
                            <BreadcrumbLink
                              href={crumb.href}
                              onClick={(e) => {
                                e.preventDefault()
                                navigate(crumb.href!)
                              }}
                            >
                              {crumb.label}
                            </BreadcrumbLink>
                          )
                        ) : (
                          <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                        )}
                      </BreadcrumbItem>
                    </React.Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
          </div>
          {isInIframe && !isShopifyEmbedded ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handlePopout}
                    className="tw-flex tw-flex-shrink-0 tw-items-center tw-justify-center tw-h-8 tw-w-8 tw-rounded-md hover:tw-bg-slate-100 tw-text-slate-600 hover:tw-text-slate-900 tw-transition-colors"
                    aria-label="Open PayHelm in new tab"
                  >
                    <ExternalLink className="tw-h-4 tw-w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Open PayHelm in new tab</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
        </header>
        <div
          className={`sidebar-content-body tw-min-w-0 tw-max-w-full${isAI ? ' sidebar-content-body--ai' : ''}`}
        >
          {children}
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}

export default SidebarLayout
